import React, { useState, useRef, useEffect, useCallback } from 'react';
import { LinkIcon, InstagramIcon } from '../../icons';
import '../Profile.css';

export interface AboutSectionProps {
  title?: string;
  text: string;
  websiteUrl?: string;
  instagramUrl?: string;
}

export const AboutSection: React.FC<AboutSectionProps> = ({
  text,
  websiteUrl,
  instagramUrl,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [truncatedText, setTruncatedText] = useState<string | null>(null);
  const measureRef = useRef<HTMLParagraphElement>(null);

  const measure = useCallback(() => {
    const el = measureRef.current;
    if (!el) return;

    const computedStyle = window.getComputedStyle(el);
    const lineHeight = parseFloat(computedStyle.lineHeight) || 20;
    const maxHeight = Math.ceil(lineHeight * 2) + 2;

    el.textContent = text;

    if (el.scrollHeight <= maxHeight) {
      setTruncatedText(null);
      return;
    }

    let low = 0;
    let high = text.length;
    const suffix = '… ';
    const seeMore = '\u00A0See more';

    while (low < high) {
      const mid = Math.floor((low + high) / 2);
      el.textContent = text.slice(0, mid) + suffix + seeMore;
      if (el.scrollHeight > maxHeight) {
        high = mid;
      } else {
        low = mid + 1;
      }
    }

    setTruncatedText(text.slice(0, Math.max(0, low - 1)).trimEnd());
  }, [text]);

  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  const needsTruncation = truncatedText !== null;

  return (
    <div className="profile-about">
      {text && (
        <>
          <p ref={measureRef} className="profile-about__text" style={{ position: 'absolute', visibility: 'hidden', left: 0, right: 0, pointerEvents: 'none' }} />
          <p className="profile-about__text">
            {expanded || !needsTruncation ? text : `${truncatedText}…\u00A0`}
            {needsTruncation && !expanded && (
              <button className="profile-about__toggle" onClick={() => setExpanded(true)}>
                See more
              </button>
            )}
          </p>
        </>
      )}
      {(websiteUrl || instagramUrl) && (
        <div className="profile-about__links">
          {websiteUrl && (
            <a href={websiteUrl} target="_blank" rel="noopener noreferrer" className="profile-about__link">
              <LinkIcon size="xs" tone="inherit" />
              <span>{websiteUrl.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}</span>
            </a>
          )}
          {instagramUrl && (
            <a href={instagramUrl} target="_blank" rel="noopener noreferrer" className="profile-about__link">
              <InstagramIcon size="xs" tone="inherit" />
              <span>@{instagramUrl.replace(/^https?:\/\/(www\.)?instagram\.com\//, '').replace(/\/$/, '')}</span>
            </a>
          )}
        </div>
      )}
    </div>
  );
};

export default AboutSection;
